import { CollisionPriority } from "@dnd-kit/abstract";
import { move } from "@dnd-kit/helpers";
import {
  DragDropProvider,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  useDroppable,
} from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { GripVertical } from "lucide-react-native";
import {
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { Question } from "@/components/questions";
import { VStack } from "@/components/ui";
import {
  Question as QuestionType,
  Section as SectionType,
  VariableDef,
} from "@/types/resources";

export interface SortableQuestionListRef {
  scrollToEnd: (animated?: boolean) => void;
  scrollToItem: (id: string) => void;
}

interface QuestionSection {
  data: QuestionType[];
  section: SectionType | null;
}

interface SortableQuestionListProps {
  disabled: boolean;
  groupedData: QuestionSection[];
  listFooter: ReactNode;
  onQuestionDeleted: (questionId: string) => void;
  onQuestionUpdated: (question: QuestionType) => void;
  onReorder: (
    question: QuestionType,
    newOrder: number,
    newSectionId: string | null
  ) => void;
  onScrollToItem: (id: string) => void;
  questions: QuestionType[];
  /** Slot rendered at every "natural" insertion point (above each
      section, before each question inside a group, after each section's
      last question, and at the very bottom). The parent decides what to
      render — typically `<AddQuestion>` or, when this gap is the active
      create target, `<CreateQuestion>`. */
  renderGap: (
    order: number,
    sectionId: string | null,
    forceDisabled?: boolean
  ) => ReactNode;
  renderSectionHeader: (group: QuestionSection) => ReactNode;
  studyId: string;
}

type GroupedItems = Record<string, string[]>;

/* ── Helpers ─────────────────────────────────────────────── */

function getContainerId(
  section: SectionType | null,
  groupIndex: number
): string {
  return section ? `section-${section.id}` : `unsectioned-${groupIndex}`;
}

function getSectionIdFromContainer(containerId: string): string | null {
  return containerId.startsWith("section-")
    ? containerId.replace("section-", "")
    : null;
}

/* ── Droppable section wrapper ───────────────────────────── */

function DroppableSection({
  children,
  disabled = false,
  id,
}: {
  children: ReactNode;
  /** When true, the section refuses incoming question drops. Used to lock
      use-case sections so authors can't move questions in or out. */
  disabled?: boolean;
  id: string;
}) {
  const { ref } = useDroppable({
    id,
    collisionPriority: CollisionPriority.Low,
    disabled,
  });

  return (
    <div ref={ref} style={{ minHeight: 20 }}>
      {children}
    </div>
  );
}

/* ── Droppable gap between sections ──────────────────────── */

function DroppableGap({
  children,
  id,
  spacing = 0,
}: {
  children?: ReactNode;
  id: string;
  spacing?: number;
}) {
  const { ref } = useDroppable({
    id,
    collisionPriority: CollisionPriority.Low,
  });

  return (
    <div ref={ref} style={{ minHeight: spacing }}>
      {children}
    </div>
  );
}

/* ── Sortable question item ──────────────────────────────── */

function SortableQuestion({
  containerId,
  disabled,
  index,
  isLast,
  locked = false,
  onDeleted,
  onPress,
  onUpdated,
  question,
  questions,
  sectionQuestionCount,
  studyId,
  variables,
}: {
  containerId: string;
  disabled: boolean;
  index: number;
  isLast: boolean;
  /** True when the parent section is a use-case template — disables
      drag-and-drop and propagates as a `disabled` prop to the `Question`
      editor so its delete/edit affordances are also off. */
  locked?: boolean;
  onDeleted: () => void;
  onPress: () => void;
  onUpdated: (q: QuestionType) => void;
  question: QuestionType;
  questions: QuestionType[];
  sectionQuestionCount: number;
  studyId: string;
  variables: VariableDef[];
}) {
  const isUnsectioned =
    containerId.startsWith("unsectioned-") || containerId.startsWith("gap-");
  const isDraggable =
    !disabled && !locked && (isUnsectioned || sectionQuestionCount > 1);
  const { ref, handleRef, isDragSource } = useSortable({
    id: question.id,
    index,
    group: containerId,
    disabled: !isDraggable,
  });

  return (
    <div
      ref={ref}
      id={`question-${question.id}`}
      style={{
        cursor: disabled ? "default" : undefined,
        opacity: isDragSource ? 0.15 : 1,
        position: "relative",
      }}
    >
      <VStack style={{ pointerEvents: isDragSource ? "none" : "auto" }}>
        <Question
          allQuestions={questions}
          disabled={disabled}
          onDeleted={onDeleted}
          onPress={onPress}
          onUpdated={onUpdated}
          question={question}
          studyId={studyId}
          variables={variables}
        />
      </VStack>
      {isDraggable && !isDragSource && (
        <div
          ref={handleRef}
          style={{
            cursor: "grab",
            padding: 6,
            position: "absolute",
            right: 6,
            top: 10,
            zIndex: 10,
          }}
        >
          <GripVertical className="text-typography-500" size={16} />
        </div>
      )}
    </div>
  );
}

/* ── Drag overlay content ────────────────────────────────── */

function DragOverlayContent({
  question,
  questions,
  studyId,
}: {
  question: QuestionType;
  questions: QuestionType[];
  studyId: string;
}) {
  return (
    <div style={{ opacity: 0.9, pointerEvents: "none" }}>
      <Question
        allQuestions={questions}
        disabled
        onDeleted={() => {}}
        onPress={() => {}}
        onUpdated={() => {}}
        question={question}
        studyId={studyId}
      />
    </div>
  );
}

/* ── Gap with sortable items ─────────────────────────────── */

function GapWithItems({
  disabled,
  gapId,
  gapQuestionIds,
  onQuestionDeleted,
  onQuestionUpdated,
  onScrollToItem,
  questions,
  questionsMap,
  spacing,
  studyId,
}: {
  disabled: boolean;
  gapId: string;
  gapQuestionIds: string[];
  onQuestionDeleted: (id: string) => void;
  onQuestionUpdated: (q: QuestionType) => void;
  onScrollToItem: (id: string) => void;
  questions: QuestionType[];
  questionsMap: Map<string, QuestionType>;
  spacing?: number;
  studyId: string;
}) {
  return (
    <DroppableGap id={gapId} spacing={spacing}>
      {gapQuestionIds.map((qId, index) => {
        const question = questionsMap.get(qId);
        if (!question) return null;

        return (
          <SortableQuestion
            key={qId}
            containerId={gapId}
            disabled={disabled}
            index={index}
            isLast={index === gapQuestionIds.length - 1}
            onDeleted={() => onQuestionDeleted(qId)}
            onPress={() => onScrollToItem(qId)}
            onUpdated={onQuestionUpdated}
            question={question}
            questions={questions}
            sectionQuestionCount={gapQuestionIds.length}
            studyId={studyId}
            variables={[]}
          />
        );
      })}
    </DroppableGap>
  );
}

/* ── Main component ──────────────────────────────────────── */

const SortableQuestionList = forwardRef<
  SortableQuestionListRef,
  SortableQuestionListProps
>(
  (
    {
      disabled,
      groupedData,
      listFooter,
      onQuestionDeleted,
      onQuestionUpdated,
      onReorder,
      onScrollToItem,
      questions,
      renderGap,
      renderSectionHeader,
      studyId,
    },
    ref
  ) => {
    const questionsMap = useMemo(() => {
      const map = new Map<string, QuestionType>();
      questions.forEach((q) => map.set(q.id, q));
      return map;
    }, [questions]);

    // Build grouped items: gap before each section + gap after last
    const buildItems = useCallback((data: QuestionSection[]): GroupedItems => {
      const result: GroupedItems = {};
      data.forEach((group, i) => {
        result[`gap-${i}`] = [];
        result[getContainerId(group.section, i)] = group.data.map((q) => q.id);
      });
      result[`gap-${data.length}`] = [];
      return result;
    }, []);

    const initialItems = useMemo(
      () => buildItems(groupedData),
      [buildItems, groupedData]
    );

    const [items, setItems] = useState<GroupedItems>(initialItems);
    const previousItemsRef = useRef<GroupedItems>(initialItems);

    // Sync when groupedData changes externally (e.g. after create/delete).
    // Track the previous prop in state (the React-recommended way to adjust
    // state during render) rather than a ref read/written during render.
    const [prevGroupedData, setPrevGroupedData] = useState(groupedData);
    if (prevGroupedData !== groupedData) {
      setPrevGroupedData(groupedData);
      setItems(initialItems);
    }

    // Keep the drag-revert snapshot in sync with external data changes. Runs
    // after commit and only when the data actually changes, so it never
    // clobbers the snapshot taken at drag start.
    useEffect(() => {
      previousItemsRef.current = initialItems;
    }, [initialItems]);

    // Track which question is being dragged for the overlay
    const [activeId, setActiveId] = useState<string | null>(null);
    const activeQuestion = activeId ? questionsMap.get(activeId) : null;

    // Ref to the scroll container
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      scrollToEnd: () => {
        const el = scrollContainerRef.current;
        if (el) {
          el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        }
      },
      scrollToItem: (id: string) => {
        const container = scrollContainerRef.current;
        const nativeId =
          id.startsWith("section-") || id.startsWith("create-question-")
            ? id
            : `question-${id}`;
        const target = document.getElementById(nativeId);
        if (container && target) {
          const containerTop = container.getBoundingClientRect().top;
          const targetTop = target.getBoundingClientRect().top;
          container.scrollTo({
            top: container.scrollTop + (targetTop - containerTop),
            behavior: "smooth",
          });
        }
      },
    }));

    const handleDragStart = useCallback(
      (event: DragStartEvent) => {
        previousItemsRef.current = items;
        const sourceId = event.operation?.source?.id;
        if (sourceId && typeof sourceId === "string") {
          setActiveId(sourceId);
        }
      },
      [items]
    );

    const handleDragOver = useCallback((event: DragOverEvent) => {
      setItems((currentItems) => move(currentItems, event));
    }, []);

    // Build ordered container list: gap, section, gap, section, ..., gap
    const containerOrder = useMemo(() => {
      const order: string[] = [];
      groupedData.forEach((g, i) => {
        order.push(`gap-${i}`);
        order.push(getContainerId(g.section, i));
      });
      order.push(`gap-${groupedData.length}`);
      return order;
    }, [groupedData]);

    // Empty sections are always ordered last, so if the final group is a
    // section with no questions the trailing root-level insert gap would
    // render just below it, outside the section. Track that to suppress it.
    const lastGroup = groupedData[groupedData.length - 1];
    const lastGroupIsEmptySection =
      !!lastGroup && lastGroup.section !== null && lastGroup.data.length === 0;

    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        setActiveId(null);

        if (event.canceled) {
          setItems(previousItemsRef.current);
          return;
        }

        const source = event.operation?.source;
        if (!source) return;

        const questionId = String(source.id);
        const question = questionsMap.get(questionId);
        if (!question) return;

        // Apply the final move
        const finalItems = move({ ...items }, event) as GroupedItems;
        setItems(finalItems);

        // Compute new global order by flattening all containers in order
        let newOrder = 0;
        let newSectionId: string | null = null;
        let found = false;

        for (const cId of containerOrder) {
          const ids = finalItems[cId] ?? [];
          for (let i = 0; i < ids.length; i++) {
            newOrder++;
            if (ids[i] === questionId) {
              newSectionId = getSectionIdFromContainer(cId);
              found = true;
              break;
            }
          }
          if (found) break;
        }

        if (!found) return;

        if (newOrder !== question.order || newSectionId !== question.section) {
          onReorder(question, newOrder, newSectionId);
        }
      },
      [containerOrder, items, onReorder, questionsMap]
    );

    return (
      <div
        ref={scrollContainerRef}
        style={{ flex: 1, overflow: "auto", paddingRight: 12 }}
      >
        <DragDropProvider
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          {groupedData.map((group, groupIndex) => {
            const containerId = getContainerId(group.section, groupIndex);
            const gapId = `gap-${groupIndex}`;
            const questionIds = items[containerId] ?? [];
            const gapQuestionIds = items[gapId] ?? [];
            // Use-case sections are template-locked: questions inside can't be
            // dragged, deleted, or accept drops from outside.
            const sectionLocked =
              group.section !== null && group.section.use_case !== null;
            /* AddQuestion (order, sectionId) anchors. For empty sections
               group.data is empty — fall back to total+1 since they're
               always rendered last in groupedData. */
            const groupStartOrder =
              group.data[0]?.order ?? questions.length + 1;
            const groupEndOrder =
              group.data.length > 0
                ? group.data[group.data.length - 1].order + 1
                : groupStartOrder;
            /* An empty section's only interactive insert point is the "+"
               INSIDE it (the append gap keyed by section id). The root-level
               gaps above and below it sit outside the section, so they're
               rendered disabled to avoid stray affordances. */
            const isEmptySection =
              group.section !== null && group.data.length === 0;

            return (
              <div key={containerId}>
                {/* DnD drop zone (kept for cross-group drops). Now
                    that `AddQuestion` is always-visible (a 20px slot
                    with its plus icon centred over a divider), the
                    leading `AddQuestion` rendered just below owns the
                    vertical rhythm between groups all on its own —
                    so this gap collapses to zero unless it actually
                    contains a dragged question, eliminating the old
                    4px of dead padding above root-level questions
                    that followed a section. */}
                <GapWithItems
                  gapId={gapId}
                  gapQuestionIds={gapQuestionIds}
                  disabled={disabled}
                  onQuestionDeleted={onQuestionDeleted}
                  onScrollToItem={onScrollToItem}
                  onQuestionUpdated={onQuestionUpdated}
                  questions={questions}
                  questionsMap={questionsMap}
                  studyId={studyId}
                />

                {/* AddQuestion: above each section header (root insert).
                    Disabled above an empty section — inserting a root
                    question there sits outside the section. */}
                {group.section &&
                  renderGap(groupStartOrder, null, isEmptySection)}

                <DroppableSection disabled={sectionLocked} id={containerId}>
                  <div
                    id={
                      group.section
                        ? `section-${group.section.id}`
                        : `unsectioned-${groupIndex}`
                    }
                  >
                    {renderSectionHeader(group)}

                    {/* Questions */}
                    <VStack>
                      <VStack
                        className={
                          group.section
                            ? "ml-3 pl-3 border-l-2 border-outline-200"
                            : ""
                        }
                      >
                        {questionIds.map((qId, index) => {
                          const question = questionsMap.get(qId);
                          if (!question) return null;

                          return (
                            <div key={qId}>
                              {/* AddQuestion: before each question (also
                                  doubles as top-of-section for the first
                                  Q in a section). Inside locked use-case
                                  sections the gap still renders so the
                                  vertical rhythm matches non-locked
                                  sections — `renderGap` disables the
                                  affordance there. */}
                              {renderGap(
                                question.order,
                                group.section?.id ?? null
                              )}

                              <SortableQuestion
                                containerId={containerId}
                                disabled={disabled}
                                index={index}
                                isLast={index === questionIds.length - 1}
                                locked={sectionLocked}
                                onDeleted={() => onQuestionDeleted(qId)}
                                onPress={() => onScrollToItem(qId)}
                                onUpdated={onQuestionUpdated}
                                question={question}
                                questions={questions}
                                sectionQuestionCount={questionIds.length}
                                studyId={studyId}
                                variables={
                                  group.section?.config?.variables ?? []
                                }
                              />
                            </div>
                          );
                        })}

                        {/* AddQuestion: append to this section. Root-level
                            "between groups" / "below last group" inserts
                            are handled by the per-section anchor above
                            and the bottom anchor below. */}
                        {group.section &&
                          renderGap(groupEndOrder, group.section.id)}
                      </VStack>
                    </VStack>
                  </div>
                </DroppableSection>
              </div>
            );
          })}

          {/* DnD drop zone after last group */}
          {(() => {
            const lastGapId = `gap-${groupedData.length}`;
            const lastGapQuestionIds = items[lastGapId] ?? [];
            return (
              <GapWithItems
                gapId={lastGapId}
                gapQuestionIds={lastGapQuestionIds}
                disabled={disabled}
                onQuestionDeleted={onQuestionDeleted}
                onScrollToItem={onScrollToItem}
                onQuestionUpdated={onQuestionUpdated}
                questions={questions}
                questionsMap={questionsMap}
                studyId={studyId}
              />
            );
          })()}

          {/* AddQuestion: under the last section / question. Kept rendered
              (so the vertical rhythm never shifts) but force-disabled when
              the study ends on an empty section — that section already owns
              an inline insert gap, and an active "+" sitting just below it
              (outside the section) reads as a stray root-level affordance. */}
          {questions.length > 0 &&
            renderGap(questions.length + 1, null, lastGroupIsEmptySection)}

          <DragOverlay>
            {activeQuestion && (
              <DragOverlayContent
                question={activeQuestion}
                questions={questions}
                studyId={studyId}
              />
            )}
          </DragOverlay>

          {/* List footer (add question/section buttons) */}
          <div style={{ marginTop: 8 }}>{listFooter}</div>

          <div style={{ height: 16 }} />
        </DragDropProvider>
      </div>
    );
  }
);

SortableQuestionList.displayName = "SortableQuestionList";

export default SortableQuestionList;
